import { useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Col,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  ModalProps,
  Row
} from 'antd'
import { useEffect } from 'react'

import {
  useStocksServiceCreateMeasurementUnitEndpoint,
  useStocksServiceGetMeasurementUnitEndpoint,
  useStocksServiceGetMeasurementUnitListEndpointKey,
  useStocksServiceUpdateMeasurementUnitEndpoint
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { LANGUAGE_KEYS, requiredRules } from 'src/app-constants'
import { EditableFormItem, message, SaveConfirm } from 'src/components'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  id?: string
  onCancel: () => void
} & ModalProps

const getModalText = (hasId: boolean) => {
  return {
    title: `medicine_register.units.${hasId ? 'edit' : 'add'}_unit`,
    button: `common.${hasId ? 'edit' : 'add'}`
  }
}

export const UnitsModal = ({ id, onCancel, ...props }: Props) => {
  const { t } = useLocales()
  const [form] = Form.useForm()

  const queryClient = useQueryClient()

  const { title, button } = getModalText(!!id)

  const { data } = useStocksServiceGetMeasurementUnitEndpoint(
    { id: String(id) },
    [id],
    { enabled: !!id }
  )

  const { mutate: create, isPending: isCreating } =
    useStocksServiceCreateMeasurementUnitEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => onMutationSuccess()
    })

  const { mutate: edit, isPending: isEditing } =
    useStocksServiceUpdateMeasurementUnitEndpoint({
      onError: (e: ApiError) => formErrorHandler(e.body.errors),
      onSuccess: () => onMutationSuccess()
    })

  const onSubmit = async () => {
    const values = await form.validateFields()

    const payload = {
      name: {
        ro: values?.name?.ro,
        ru: values?.name?.ru
      },
      comments: values?.comments,
      order: values?.order
    }

    id ? edit({ requestBody: payload, id }) : create({ requestBody: payload })
  }

  const onMutationSuccess = () => {
    message.success(t('common.save_success'))
    queryClient.invalidateQueries({
      queryKey: [useStocksServiceGetMeasurementUnitListEndpointKey]
    })
    onCancel()
  }

  useEffect(() => {
    if (data && id) {
      form.setFieldsValue({
        name: {
          ro: data?.name?.ro,
          ru: data?.name?.ru
        },
        comments: data?.comments,
        order: data?.order
      })
    }
  }, [data, id])

  return (
    <Modal
      title={t(title)}
      open
      centered
      footer={null}
      maskClosable={false}
      onCancel={onCancel}
      afterClose={form.resetFields}
      {...props}
    >
      <Form layout="vertical" requiredMark={false} form={form}>
        <Row gutter={[24, 24]}>
          {LANGUAGE_KEYS.map((langKey) => {
            return (
              <Col span={12} key={langKey}>
                <EditableFormItem
                  isEditing={true}
                  name={['name', langKey]}
                  label={`${langKey.toUpperCase()}`}
                  rules={requiredRules}
                  className="full-width"
                >
                  <Input placeholder={t('common.enter_text')} />
                </EditableFormItem>
              </Col>
            )
          })}

          <Col span={12}>
            <Form.Item name="order" label={t('common.order')}>
              <InputNumber
                className="full-width"
                placeholder={t('common.order')}
              />
            </Form.Item>
          </Col>

          <Col span={24}>
            <Form.Item name="comments" label={t('common.comments')}>
              <Input.TextArea rows={5} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Flex justify="center" gap={8}>
              <Button onClick={onCancel}>{t('common.cancel')}</Button>

              <SaveConfirm
                title={`${t(button)}?`}
                onConfirm={onSubmit}
                okButtonProps={{ loading: isCreating || isEditing }}
              >
                <Button type="primary">{t(button)}</Button>
              </SaveConfirm>
            </Flex>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}
